// Shared in-memory row comparator for the bulk-import listing pages. These pages fetch the
// full result set up front and sort/page client-side, so they need a consistent comparison
// policy: a missing value is treated as the smallest value (sorting first when ascending and
// last when descending), numbers and dates compare by value, and everything else compares as
// a case-insensitive string.

const defaultArray = [];

// Builds a comparator for the chosen field and direction. When the primary field ties and a
// tieBreakField is supplied, the rows fall back to that field so equal values have a stable,
// predictable secondary order. The fallback follows the same sort direction as the primary
// column, and is skipped when the tie-break field is already the primary sort.
export function buildComparator(
    {
        sortField,
        direction,
        numericSortFields = defaultArray,
        dateSortFields = defaultArray,
        tieBreakField = null
    }
) {
    const multiplier = direction === "D" ? -1 : 1;

    function compareValues(valueA, valueB, field) {
        const aMissing = valueA == null;
        const bMissing = valueB == null;
        if (aMissing && bMissing) {
            return 0;
        }
        if (aMissing || bMissing) {
            return (aMissing ? -1 : 1) * multiplier;
        }

        let comparison;
        if (numericSortFields.includes(field)) {
            comparison = valueA - valueB;
        } else if (dateSortFields.includes(field)) {
            comparison = new Date(valueA).getTime() - new Date(valueB).getTime();
        } else {
            comparison = String(valueA).toLowerCase().localeCompare(String(valueB).toLowerCase());
        }

        return comparison * multiplier;
    }

    return function (rowA, rowB) {
        const primaryComparison = compareValues(rowA[sortField], rowB[sortField], sortField);
        if (primaryComparison !== 0 || tieBreakField == null || sortField === tieBreakField) {
            return primaryComparison;
        }

        return compareValues(rowA[tieBreakField], rowB[tieBreakField], tieBreakField);
    };
}
